/**
 * introOverrides — the SimplePlayer runtime overrides that assemble a
 * presentation intro from pieces (see introAssembly.ts). Kept out of
 * LearningPlayer.vue so that file only carries the wiring.
 */
import { apiUrl } from '@/platform/apiBase'
import type { Cycle } from '@ssi/core'
import { TOM_INTRO_BANK } from './introBank'
import { assembleIntroMemoised, getIntroPieceGapMs, planIntroPieces } from './introAssembly'

export interface IntroOverrideCtx {
  getCourseCode: () => string | null | undefined
  /** voice_config.voices.known.voiceId of the playing course. */
  getKnownVoiceId: () => string | null | undefined
  /** The same resolver the engine uses, so offline-cached pieces are read locally. */
  resolveUrl: (url: string) => Promise<string>
  /** Optional "as in" context for a LEGO (score table, job #543). Absent = none. */
  getContext?: (legoId: string) => { audioUrl: string; score: number } | null
}

const introAssembleFlag = (): string | null => {
  try { return new URLSearchParams(globalThis.location?.search ?? '').get('introAssemble') } catch { return null }
}

/** `?introAssemble=all` — audition: assemble for any English-known course, voice-matched or not. */
function auditionAll(): boolean { return introAssembleFlag() === 'all' }

/**
 * `?introAssemble=tom` — test round (job #310): splice the intro from Tom-006 pieces
 * (TOM_INTRO_BANK) around the Tom-006 LEGO prompt; courses the Tom bank lacks play their clip.
 */
function tomPieces(): boolean { return introAssembleFlag() === 'tom' }

/**
 * The LEGO's own prompt clip. `known.fallbackUrl` carries it when the cycle
 * also has a presentation narration; with no narration `known.audioUrl` IS it.
 */
export function legoPromptUrlOf(cycle: Cycle): string | undefined {
  return cycle.known?.fallbackUrl || cycle.known?.audioUrl || undefined
}

/**
 * Assembly splices the narration around the LEGO's own clip, so it needs that
 * clip. Without `known.fallbackUrl` the single `audioUrl` is the narration
 * itself, not a bare prompt, and splicing it would say it twice.
 */
const hasLegoClip = (cycle: Cycle): boolean => !!cycle.known?.fallbackUrl

export function createIntroOverrides(ctx: IntroOverrideCtx) {
  // Tom 2026-10-12 (presentation-intros-whole-phrase-reads): a linked whole
  // presentation clip (course_legos.presentation_audio_id) ALWAYS wins. Pieces
  // would splice Charlotte bank clips over a whole Tom-006 read, so assembly
  // runs only in audition (?introAssemble=all); the live path plays the clip.
  const pieceUrls = (cycle: Cycle): string[] | null =>
    !(auditionAll() || tomPieces()) || !hasLegoClip(cycle) ? null : planIntroPieces({
      courseCode: ctx.getCourseCode(),
      knownVoiceId: ctx.getKnownVoiceId(),
      legoId: cycle.legoId,
      legoPromptUrl: cycle.known?.fallbackUrl,
      audioUrl: (id) => apiUrl(`/api/audio/${id}`),
      context: ctx.getContext?.(cycle.legoId) ?? null,
      ignoreVoiceMatch: true,
      bank: tomPieces() ? TOM_INTRO_BANK : undefined,
    })

  const assemble = (cycle: Cycle): Promise<string | null> => {
    const urls = pieceUrls(cycle)
    return urls
      ? assembleIntroMemoised(urls, getIntroPieceGapMs(), { resolveUrl: ctx.resolveUrl, tomEnding: tomPieces() })
      : Promise.resolve(null)
  }

  return {
    assembleIntroPrompt: assemble,
    prewarmIntroPrompt: (cycle: Cycle) => { void assemble(cycle) },
  }
}
